import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Time } from 'lightweight-charts';
import TvChart, { MA_DEFS, kstEpoch, type Bar } from './TvChart';
import TradingAccountGrids from './TradingAccountGrids';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import {
  page, cardGrid, titleRow, gridTitle, gridHint, btnPrimary, selectSm, emptyMsg, signColor, won, pct, colors,
} from '../styles/fdsTheme';

/**
 * 트레이딩 화면 (2026-11-03) — TradingView · Trading212 배치. 배치 A (사용자 선택):
 *
 *   ┌ 관심종목 ───────┐┌──── 차트 (TradingView lightweight-charts) ────┐
 *   │ [그룹][그룹]…   ││ 캔들 + 거래량 + MA5/20/60, 1·5·10·30분/일     │
 *   │ 종목 · 종가 · % │├───────────────────────────────────────────────┤
 *   ├ 주문 ───────────┤│ [보유종목] [오늘 주문 — 취소]                  │
 *   │ 매수/매도 …     ││                                               │
 *   ├ 호가 10단 ──────┤│                                               │
 *   └─────────────────┘└───────────────────────────────────────────────┘
 *
 * 주문은 **확인창 → 기존 주문 API(POST /api/v1/orders/add)** 로 kispforder 에 넣는다.
 * 그 뒤는 주문루프(fdsorder)가 KIS 에 낸다 — 이 화면이 KIS 주문을 직접 치지 않는다.
 * 실전 계좌 발주 차단·계좌/슬롯 권한·선물 검증은 서버가 한다.
 *
 * 호가는 KIS 호가 조회(/stocks/asking-price-exp-ccn) — 종목 선택 시 + 5초마다. 호가 가격을 누르면 지정가에 들어간다.
 */

type Period = '1m' | '5m' | '10m' | '30m' | 'D';
const PERIODS: { key: Period; label: string; minute: number; days: number }[] = [
  { key: '1m', label: '1분', minute: 1, days: 1 },
  { key: '5m', label: '5분', minute: 5, days: 5 },
  { key: '10m', label: '10분', minute: 10, days: 10 },
  { key: '30m', label: '30분', minute: 30, days: 20 },
  { key: 'D', label: '일', minute: 0, days: 365 },
];
const BOOK_REFRESH_MS = 5000;
const ACCOUNT_REFRESH_MS = 10000;

interface WatchRow { prodId: string; prodNm: string; last?: number; chg?: number; }
interface Book { asks: { p: number; q: number }[]; bids: { p: number; q: number }[]; totAsk: number; totBid: number; hour?: string; }

const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n: number) => fmtDate(new Date(Date.now() - n * 86400000));
const arr = (d: any): any[] => (Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []);
const num = (v: any) => Number(String(v ?? '0').replace(/,/g, '')) || 0;
const hhmm = () => { const n = new Date(); return `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`; };
const errText = (e: any) => e?.response?.data?.message ?? e?.response?.data?.error ?? e?.response?.data?.errors?.join?.(', ') ?? e?.message ?? '알 수 없는 오류';

const TradingViewSample: React.FC = () => {
  const axios = getMainAxios();
  const { selectedPfId, portfolios, canOrder } = useAuth();
  const pf = portfolios.find(p => p.id === selectedPfId);
  const isFut = pf?.acntType === 'fut';

  const [groups, setGroups] = useState<{ id: number; name: string }[]>([]);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [watch, setWatch] = useState<WatchRow[]>([]);
  const [prodId, setProdId] = useState<string>('');
  const [period, setPeriod] = useState<Period>('D');
  const [bars, setBars] = useState<Bar[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [positions, setPositions] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [book, setBook] = useState<Book | null>(null);
  const [bookErr, setBookErr] = useState<string | null>(null);

  // 주문 입력
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [ordType, setOrdType] = useState<'limit' | 'market'>('limit');
  const [qty, setQty] = useState(1);
  const [prc, setPrc] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [slots, setSlots] = useState<{ id: number; name: string }[]>([]);
  const [slotId, setSlotId] = useState<number | null>(null);

  // 관심그룹
  useEffect(() => {
    axios.get('/api/v1/watchlist/groups').then(r => {
      const g = arr(r.data);
      setGroups(g);
      if (g.length) setGroupId(g[0].id);
    }).catch(() => setGroups([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 그룹 종목 + 종목명 + 최근 종가(일봉 DB)
  useEffect(() => {
    if (groupId == null) return;
    (async () => {
      try {
        const ids: string[] = arr((await axios.get(`/api/v1/watchlist/groups/${groupId}/items`)).data).map((i: any) => i.prodId);
        if (!ids.length) { setWatch([]); return; }
        const nmRes = (await axios.get(`/api/v1/stocks/prod-nms?${ids.map(id => `prodIds=${encodeURIComponent(id)}`).join('&')}`)).data;
        const nms: Record<string, string> = nmRes?.data ?? nmRes ?? {};
        const rows: WatchRow[] = ids.map(id => ({ prodId: id, prodNm: nms[id] || id }));
        setWatch(rows);
        setProdId(p => (p && ids.includes(p) ? p : ids[0]));
        // 종가·등락률 — 종목당 일봉 1회 (DB). 선물 코드는 일봉 원천이 달라 비워 둔다
        setWatch(await Promise.all(rows.map(async w => {
          if (!/^[0-9Q]/.test(w.prodId)) return w;
          try {
            const h = arr((await axios.get('/api/v1/quant/screener/history', { params: { prodId: w.prodId } })).data);
            const n = h.length;
            return n < 2 ? w : { ...w, last: h[n - 1].c, chg: h[n - 2].c ? (h[n - 1].c / h[n - 2].c - 1) * 100 : 0 };
          } catch { return w; }
        })));
      } catch { setWatch([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // 차트
  const pdef = PERIODS.find(p => p.key === period)!;
  useEffect(() => {
    if (!prodId) return;
    setLoading(true);
    (async () => {
      try {
        if (pdef.minute > 0) {
          const r = await axios.get('/api/v1/quant/intraday-bars', {
            params: { prodId, from: daysAgo(pdef.days + 3), to: fmtDate(new Date()), minute: pdef.minute },
          });
          setBars(arr(r.data).map((b: any) => ({ time: kstEpoch(String(b.d), String(b.t)), open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v })));
        } else {
          const r = await axios.get('/api/v1/quant/screener/history', { params: { prodId } });
          setBars(arr(r.data).map((b: any) => ({ time: b.date as Time, open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v })));
        }
      } catch { setBars([]); } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodId, period]);

  // 호가 — 주식 코드만 (KIS 주식 호가 API). 탭이 안 보일 때는 쉬어 호출 수를 아낀다
  useEffect(() => {
    setBook(null); setBookErr(null);
    if (!prodId || !/^[0-9Q]/.test(prodId)) { setBookErr('주식 종목만 호가를 보여줍니다'); return; }
    let alive = true;
    const load = async () => {
      if (document.hidden) return;
      try {
        const r = await axios.get(`/api/v1/stocks/asking-price-exp-ccn/${prodId}`);
        const a = r.data?.askingPrice;
        if (!alive || !a) return;
        const asks = (a.askPrices ?? []).map((p: any, i: number) => ({ p: num(p), q: num(a.askQuantities?.[i]) }));
        const bids = (a.bidPrices ?? []).map((p: any, i: number) => ({ p: num(p), q: num(a.bidQuantities?.[i]) }));
        setBook({ asks, bids, totAsk: num(a.totalAskQuantity), totBid: num(a.totalBidQuantity), hour: a.asprAcptHour });
        setBookErr(null);
      } catch (e) { if (alive) setBookErr(`호가 조회 실패: ${errText(e)}`); }
    };
    load();
    const h = window.setInterval(load, BOOK_REFRESH_MS);
    return () => { alive = false; window.clearInterval(h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodId]);

  // 보유·주문 (선택 계좌)
  const loadAccount = useCallback(() => {
    if (!selectedPfId) return;
    const d = fmtDate(new Date());
    axios.get('/api/v1/portfolio/positions', { params: { id: selectedPfId, d } }).then(r => setPositions(arr(r.data))).catch(() => setPositions([]));
    axios.get('/api/v1/portfolio/orders', { params: { id: selectedPfId, d0: d, d1: d } }).then(r => setOrders(arr(r.data))).catch(() => setOrders([]));
    setRefreshKey(k => k + 1);   // 슬라이스·체결·손익 탭도 같이 다시 읽는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPfId]);
  useEffect(() => { loadAccount(); }, [loadAccount]);
  // 장중(08:30~16:00)에는 10초마다 다시 읽는다 — 화면이 안 보이면 쉰다
  useEffect(() => {
    const h = window.setInterval(() => {
      const hm = hhmm();
      if (!document.hidden && hm >= '08:30' && hm <= '16:00') loadAccount();
    }, ACCOUNT_REFRESH_MS);
    return () => window.clearInterval(h);
  }, [loadAccount]);

  // 주문 슬롯 — 슬롯 단위 권한이면 그 중에서 반드시 고른다. 계좌 전체 권한이면 '기본(MANUAL)' + 운용 슬롯
  const orderSlots = pf?.orderSlots ?? null;
  useEffect(() => {
    if (!selectedPfId) return;
    axios.get('/api/v1/pfstrat/slots', { params: { pfId: selectedPfId } }).then(r => {
      const all = ((r.data?.slots ?? []) as { id: number; name: string; enabled: boolean; isSystem: boolean }[])
        .filter(s => s.enabled && !s.isSystem && (!orderSlots || orderSlots.includes(s.id)));
      setSlots(all.map(s => ({ id: s.id, name: s.name })));
      setSlotId(orderSlots ? (all[0]?.id ?? null) : null);
    }).catch(() => setSlots([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPfId, orderSlots?.join(',')]);

  const last = bars.length ? bars[bars.length - 1] : undefined;
  const cur = watch.find(w => w.prodId === prodId);
  // 관심종목 밖 종목(보유·주문에서 고른 것)도 이름이 보이게
  const prodNm = cur?.prodNm
    ?? positions.find(p => (p.prodid ?? p.prodId) === prodId)?.prodNm
    ?? orders.find(o => o.prodId === prodId)?.prodNm
    ?? prodId;
  const chgPct = cur?.chg ?? (pdef.minute === 0 && bars.length > 1 && bars[bars.length - 2].close
    ? (last!.close / bars[bars.length - 2].close - 1) * 100 : undefined);
  const heldPos = positions.find(p => (p.prodid ?? p.prodId) === prodId);
  const heldQty = heldPos?.pos ?? 0;
  const sellable = heldPos?.ordpsblqty ?? heldQty;
  const refPrc = book?.asks?.[0]?.p || last?.close || 0;

  // 종목이 바뀌면 지정가를 현재가로
  useEffect(() => { setPrc(Math.round(book?.bids?.[0]?.p || last?.close || 0)); setConfirming(false); },
    [prodId, last?.close]);  // eslint-disable-line react-hooks/exhaustive-deps

  const estAmt = useMemo(() => qty * (ordType === 'market' ? refPrc : prc), [qty, prc, ordType, refPrc]);
  const sideColor = side === 'buy' ? colors.up : colors.down;

  // 확인 전에 막을 것 — 서버도 검사하지만 여기서 먼저 이유를 보여준다
  const blockReason = !selectedPfId ? '계좌를 선택하세요'
    : !canOrder ? '이 계좌에 주문 권한이 없습니다'
    : pf?.isReal ? '실전 계좌 발주는 서버에서 막혀 있습니다'
    : !prodId ? '종목을 선택하세요'
    : isFut !== !/^[0-9Q]/.test(prodId) ? (isFut ? '선물 계좌에는 선물 코드(예: A01612)만 주문할 수 있습니다' : '주식 계좌에는 주식 종목만 주문할 수 있습니다')
    : qty <= 0 ? '수량을 입력하세요'
    : ordType === 'limit' && prc <= 0 ? '지정가를 입력하세요'
    : side === 'sell' && !isFut && qty > sellable ? `매도가능수량(${won(sellable)})을 넘습니다`
    : orderSlots && !slotId ? '주문할 슬롯을 선택하세요'
    : null;

  const submit = async () => {
    if (blockReason || sending) return;
    setSending(true);
    try {
      await axios.post('/api/v1/orders/add', {
        d: fmtDate(new Date()),
        pfId: selectedPfId,
        prodId,
        ls: 1,                                 // 롱 고정 — 매수=진입(1) · 매도=청산(-1) (포트폴리오 화면과 동일)
        ex: side === 'buy' ? 1 : -1,
        wei: 0,
        qty,
        exitAll: false,
        limitPrc: ordType === 'limit' ? prc : null,
        limitIdx: null,                        // 지정가·시장가만 — 상대호가 n틱은 포트폴리오 화면에서
        stopPrc: null,
        hm0: hhmm(),
        hm1: null,
        nSlices: null,
        msg: '[트레이딩화면]',
        pfStratId: slotId,
      });
      setNotice({ ok: true, text: `${prodNm} ${side === 'buy' ? '매수' : '매도'} ${won(qty)}주 주문을 넣었습니다 — 주문루프가 곧 발주합니다` });
      setConfirming(false);
      loadAccount();
      window.setTimeout(loadAccount, 4000);   // 접수·체결 반영을 한 번 더 읽는다
    } catch (e) {
      setNotice({ ok: false, text: `주문 실패: ${errText(e)}` });
    } finally { setSending(false); }
  };

  const cancelOrder = async (o: any) => {
    if (!window.confirm(`주문을 취소할까요?\n${o.prodNm ?? o.prodId} · 주문 ${won(o.qty2ord ?? o.qty2Ord)}주 · ID ${o.id}`)) return;
    try {
      await axios.post('/api/v1/orders/cancel', { ordId: o.id });
      setNotice({ ok: true, text: `주문 ${o.id} 취소 요청을 넣었습니다` });
      loadAccount();
    } catch (e) { setNotice({ ok: false, text: `취소 실패: ${errText(e)}` }); }
  };

  // ── 스타일 ──
  const tabBtn = (on: boolean, color: string = colors.btnPrimary): React.CSSProperties => ({
    flex: 1, padding: '6px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${on ? color : colors.inputBorder}`, borderRadius: 3,
    background: on ? color : colors.cardBg, color: on ? colors.cardBg : colors.body,
  });
  const inp: React.CSSProperties = { ...selectSm, width: '100%', minWidth: 0, boxSizing: 'border-box' };
  const th: React.CSSProperties = { padding: '6px 8px', fontSize: 11, color: colors.muted, fontWeight: 600, borderBottom: `1px solid ${colors.divider}`, textAlign: 'right' };
  const tdBase: React.CSSProperties = { padding: '6px 8px', fontSize: 12, color: colors.body, borderBottom: `1px solid ${colors.dividerLight}`, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const td = tdBase;
  const box: React.CSSProperties = { ...cardGrid, marginBottom: 16 };

  // 호가 막대 폭 기준
  const maxQ = Math.max(1, ...(book?.asks ?? []).map(x => x.q), ...(book?.bids ?? []).map(x => x.q));
  const bookRow = (p: number, q: number, isAsk: boolean, i: number) => {
    const td = { ...tdBase, padding: '3px 8px' };
    const w = `${(q / maxQ) * 100}%`;
    const bar = isAsk ? 'rgba(37,99,235,0.12)' : 'rgba(220,38,38,0.12)';
    return (
      <tr key={`${isAsk ? 'a' : 'b'}${i}`} style={{ cursor: p ? 'pointer' : 'default' }}
        onClick={() => { if (p) { setOrdType('limit'); setPrc(p); setConfirming(false); } }} title={p ? '눌러서 지정가로' : undefined}>
        <td style={{ ...td, position: 'relative', color: colors.down }}>
          {isAsk && <><span style={{ position: 'absolute', right: 0, top: 2, bottom: 2, width: w, background: bar }} /><span style={{ position: 'relative' }}>{q ? won(q) : ''}</span></>}
        </td>
        <td style={{ ...td, textAlign: 'center', fontWeight: 600, color: signColor(p - (last?.close ?? p)), background: p === prc && ordType === 'limit' ? colors.rowHover : undefined }}>
          {p ? won(p) : '-'}
        </td>
        <td style={{ ...td, position: 'relative', textAlign: 'left', color: colors.up }}>
          {!isAsk && <><span style={{ position: 'absolute', left: 0, top: 2, bottom: 2, width: w, background: bar }} /><span style={{ position: 'relative' }}>{q ? won(q) : ''}</span></>}
        </td>
      </tr>
    );
  };

  return (
    <div style={page}>
    <div style={{ display: 'grid', gridTemplateColumns: '300px minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
      {/* ── 좌측: 관심종목 · 주문 · 호가 ── */}
      <div style={{ minWidth: 0 }}>
        {/* 관심종목 + 관심그룹 목록 */}
        <div style={{ ...box, padding: 0, overflow: 'hidden' }}>
          <div style={{ ...titleRow, padding: '12px 12px 8px', marginBottom: 0 }}>
            <h3 style={gridTitle}>관심종목 <span style={gridHint}>({watch.length})</span></h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '0 12px 8px' }}>
            {groups.map(g => (
              <button key={g.id} onClick={() => setGroupId(g.id)}
                style={{ ...tabBtn(g.id === groupId), flex: 'none', padding: '3px 10px', fontWeight: 500 }}>{g.name}</button>
            ))}
            {groups.length === 0 && <span style={emptyMsg}>관심그룹이 없습니다</span>}
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {watch.length === 0 && <div style={{ ...emptyMsg, padding: 12 }}>종목이 없습니다</div>}
            {watch.map(w => {
              const on = w.prodId === prodId;
              return (
                <div key={w.prodId} onClick={() => setProdId(w.prodId)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', gap: 8, padding: '7px 12px', cursor: 'pointer',
                    borderTop: `1px solid ${colors.dividerLight}`,
                    borderLeft: `3px solid ${on ? colors.btnPrimary : 'transparent'}`,
                    background: on ? colors.rowHover : undefined,
                  }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: colors.flat, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.prodNm}</div>
                    <div style={{ fontSize: 11, color: colors.hint }}>{w.prodId}</div>
                  </div>
                  <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, color: colors.flat }}>{w.last != null ? won(w.last) : '-'}</div>
                    <div style={{ fontSize: 11, color: signColor(w.chg) }}>{w.chg != null ? pct(w.chg) : ''}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 주문 */}
        <div style={box}>
          <div style={titleRow}>
            <h3 style={gridTitle}>주문 <span style={gridHint}>계좌 {selectedPfId ?? '-'} {pf?.idNm ?? ''}</span></h3>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <button style={tabBtn(side === 'buy', colors.up)} onClick={() => { setSide('buy'); setConfirming(false); }}>매수</button>
            <button style={tabBtn(side === 'sell', colors.down)} onClick={() => { setSide('sell'); setConfirming(false); }}>매도</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '64px minmax(0,1fr)', gap: 8, alignItems: 'center', fontSize: 12, color: colors.body }}>
            <span>종목</span><b style={{ color: colors.flat, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prodNm}</b>
            {(slots.length > 0 || orderSlots) && <>
              <span>슬롯</span>
              <select style={inp} value={slotId ?? ''} onChange={e => setSlotId(e.target.value ? Number(e.target.value) : null)}>
                {!orderSlots && <option value="">기본 (MANUAL)</option>}
                {slots.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </>}
            <span>유형</span>
            <select style={inp} value={ordType} onChange={e => { setOrdType(e.target.value as 'limit' | 'market'); setConfirming(false); }}>
              <option value="limit">지정가</option><option value="market">시장가</option>
            </select>
            <span>가격</span>
            <input type="number" value={ordType === 'market' ? '' : prc} disabled={ordType === 'market'}
              placeholder="시장가" onChange={e => { setPrc(Number(e.target.value)); setConfirming(false); }}
              style={{ ...inp, textAlign: 'right' }} />
            <span>수량</span>
            <div style={{ display: 'flex', gap: 4, minWidth: 0 }}>
              <button style={{ ...tabBtn(false), flex: 'none', width: 28 }} onClick={() => { setQty(q => Math.max(1, q - 1)); setConfirming(false); }}>−</button>
              <input type="number" min={1} value={qty} onChange={e => { setQty(Math.max(1, Number(e.target.value))); setConfirming(false); }}
                style={{ ...inp, flex: 1, textAlign: 'right' }} />
              <button style={{ ...tabBtn(false), flex: 'none', width: 28 }} onClick={() => { setQty(q => q + 1); setConfirming(false); }}>+</button>
            </div>
            <span>보유</span>
            <span style={{ textAlign: 'right' }}>
              {won(heldQty)}{isFut ? '계약' : '주'}
              {side === 'sell' && !isFut && <button style={{ ...tabBtn(false), flex: 'none', padding: '1px 6px', marginLeft: 6 }}
                onClick={() => { setQty(Math.max(1, sellable)); setConfirming(false); }}>전량</button>}
            </span>
            <span>예상금액</span><b style={{ textAlign: 'right', color: sideColor }}>{won(estAmt)}원</b>
          </div>

          {!confirming ? (
            <button style={{ ...btnPrimary, width: '100%', marginTop: 14, padding: '8px 0', background: blockReason ? colors.btnDisabled : sideColor, cursor: blockReason ? 'not-allowed' : 'pointer' }}
              disabled={!!blockReason} title={blockReason ?? undefined} onClick={() => { setNotice(null); setConfirming(true); }}>
              {side === 'buy' ? '매수' : '매도'} 주문
            </button>
          ) : (
            <div style={{ marginTop: 14, padding: 10, border: `1px solid ${sideColor}`, borderRadius: 4, fontSize: 12, color: colors.body }}>
              <div style={{ fontWeight: 600, color: sideColor, marginBottom: 6 }}>{side === 'buy' ? '매수' : '매도'} 주문 확인</div>
              <div>{prodNm} ({prodId})</div>
              <div>{ordType === 'market' ? '시장가' : `지정가 ${won(prc)}원`} · {won(qty)}{isFut ? '계약' : '주'} · 예상 {won(estAmt)}원</div>
              <div style={{ color: colors.muted }}>계좌 {selectedPfId} {pf?.idNm ?? ''} · 슬롯 {slots.find(s => s.id === slotId)?.name ?? '기본'}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button style={tabBtn(false)} onClick={() => setConfirming(false)} disabled={sending}>취소</button>
                <button style={tabBtn(true, sideColor)} onClick={submit} disabled={sending}>{sending ? '전송 중...' : '확인 — 발주'}</button>
              </div>
            </div>
          )}
          {blockReason && !confirming && <div style={{ marginTop: 8, fontSize: 11, color: colors.hint }}>{blockReason}</div>}
          {notice && <div style={{ marginTop: 8, fontSize: 12, color: notice.ok ? colors.title : colors.up }}>{notice.text}</div>}
        </div>

        {/* 호가 */}
        <div style={box}>
          <div style={titleRow}>
            <h3 style={gridTitle}>호가 <span style={gridHint}>10단 · {BOOK_REFRESH_MS / 1000}초 갱신 · 가격 클릭 = 지정가</span></h3>
            {book?.hour && <span style={{ fontSize: 11, color: colors.muted }}>{book.hour.replace(/(\d\d)(\d\d)(\d\d)/, '$1:$2:$3')}</span>}
          </div>
          {bookErr ? <div style={emptyMsg}>{bookErr}</div> : !book ? <div style={emptyMsg}>호가 불러오는 중...</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead><tr>
                <th style={{ ...th, color: colors.down }}>매도잔량</th><th style={{ ...th, textAlign: 'center' }}>호가</th><th style={{ ...th, textAlign: 'left', color: colors.up }}>매수잔량</th>
              </tr></thead>
              <tbody>
                {[...book.asks].reverse().map((x, i) => bookRow(x.p, x.q, true, i))}
                {book.bids.map((x, i) => bookRow(x.p, x.q, false, i))}
                <tr>
                  <td style={{ ...td, fontWeight: 600, color: colors.down }}>{won(book.totAsk)}</td>
                  <td style={{ ...td, textAlign: 'center', color: colors.muted }}>합계</td>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: colors.up }}>{won(book.totBid)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── 우측: 차트 · 보유/주문 ── */}
      <div style={{ minWidth: 0 }}>
        <div style={box}>
          <div style={{ ...titleRow, alignItems: 'flex-end' }}>
            <div>
              <h3 style={gridTitle}>{prodNm} <span style={gridHint}>{prodId} {loading ? '(조회 중...)' : ''}</span></h3>
              {last && (
                <div style={{ marginTop: 4, display: 'flex', gap: 10, alignItems: 'baseline', fontVariantNumeric: 'tabular-nums' }}>
                  <b style={{ fontSize: 18, color: signColor(chgPct) }}>{won(cur?.last ?? last.close)}</b>
                  <span style={{ fontSize: 12, color: signColor(chgPct) }}>{chgPct != null ? pct(chgPct) : ''}</span>
                  <span style={{ fontSize: 11, color: colors.muted }}>
                    O {won(last.open)} · H {won(last.high)} · L {won(last.low)} · V {won(last.volume)}
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {PERIODS.map(p => (
                <button key={p.key} onClick={() => setPeriod(p.key)}
                  style={{ ...tabBtn(period === p.key), flex: 'none', padding: '3px 10px' }}>{p.label}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 11, color: colors.muted, marginBottom: 4 }}>
            {MA_DEFS.map(m => <span key={m.period}><span style={{ color: m.color }}>━</span> MA{m.period}</span>)}
          </div>
          {bars.length === 0 && !loading
            ? <div style={{ ...emptyMsg, height: 460, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>차트 데이터가 없습니다</div>
            : <TvChart bars={bars} intraday={pdef.minute > 0} height={460} />}
        </div>

        <div style={box}>
          <div style={{ ...titleRow, marginBottom: 8 }}>
            <h3 style={gridTitle}>계좌 {selectedPfId ?? '-'} {pf?.idNm ?? ''} <span style={gridHint}>오늘 주문·슬라이스·체결·보유·손익 — 장중 {ACCOUNT_REFRESH_MS / 1000}초 갱신 · 주문 행 클릭 = 그 주문만</span></h3>
            <button style={{ ...tabBtn(false), flex: 'none', width: 70 }} onClick={loadAccount}>새로고침</button>
          </div>
          <TradingAccountGrids pfId={selectedPfId} d={fmtDate(new Date())} orders={orders} positions={positions}
            refreshKey={refreshKey} isFut={isFut} onPickProd={setProdId} onCancel={cancelOrder} />
        </div>
      </div>
    </div>
    </div>
  );
};

export default TradingViewSample;
